import apiClient from '../utils/api/ApiClient';
import { API_ENDPOINTS } from '../constants/api.constants';
import type {
    CpmApproveRequest,
    CpmBarcodeDryRunResult,
    CpmBarcodeResolveRequest,
    CpmBulkApproveRequest,
    CpmBulkResolveRequest,
    CpmBulkResult,
    CpmBulkUpsertItem,
    CpmBulkUpsertResponse,
    CpmCreateExciseCategoryRequest,
    CpmDeleteExciseCategoryRequest,
    CpmExciseCategory,
    CpmMasterDetailsRequest,
    CpmMasterDetailsResponse,
    CpmMasterListRequest,
    CpmMasterListResponse,
    CpmMergeRequest,
    CpmQueueCountsResponse,
    CpmQueueListRequest,
    CpmQueueListResponse,
    CpmQueueStoreBrandOptionsRequest,
    CpmQueueStoreBrandOptionsResponse,
    CpmRefreshExciseCategoryVolumesRequest,
    CpmRejectPreviewResponse,
    CpmRejectRequest,
    CpmResolveLinkRequest,
    CpmState,
    CpmStatsResponse,
    CpmUpdateExciseCategoryRequest,
    CpmUpdateExciseCategoryStatusRequest,
    CpmUpdateStateLicenseTypesRequest,
    CpmUpdateStateLiveStatusRequest,
    CpmVolumeSetting,
} from '../types/centralProductMaster.types';

type Envelope<T> = { status: string; message?: string; data: T };

const CPM = API_ENDPOINTS.CENTRAL_PRODUCT_MASTER;

export async function getProductMasterStats(): Promise<CpmStatsResponse> {
    const response = await apiClient.get<Envelope<CpmStatsResponse>>(CPM.STATS);
    return response.data;
}

export async function retryBatchJob(id: string): Promise<unknown> {
    const response = await apiClient.post<Envelope<unknown>>(CPM.BATCH_JOB_RETRY(id), {});
    return response.data;
}

export async function listVerificationQueue(
    body: CpmQueueListRequest
): Promise<CpmQueueListResponse> {
    const response = await apiClient.post<Envelope<CpmQueueListResponse>>(CPM.QUEUE_LIST, body);
    return response.data;
}

export async function getQueueCounts(): Promise<CpmQueueCountsResponse> {
    const response = await apiClient.get<Envelope<CpmQueueCountsResponse>>(CPM.QUEUE_COUNTS);
    return response.data;
}

export async function listQueueStoreBrandOptions(
    body: CpmQueueStoreBrandOptionsRequest
): Promise<CpmQueueStoreBrandOptionsResponse> {
    const response = await apiClient.post<Envelope<CpmQueueStoreBrandOptionsResponse>>(
        CPM.QUEUE_STORE_BRAND_OPTIONS,
        body
    );
    return response.data;
}

export async function approveQueueItem(body: CpmApproveRequest) {
    const response = await apiClient.post<Envelope<unknown>>(CPM.QUEUE_APPROVE, body);
    return response.data;
}

export async function bulkApproveQueue(body: CpmBulkApproveRequest): Promise<CpmBulkResult> {
    const response = await apiClient.post<Envelope<CpmBulkResult>>(CPM.QUEUE_BULK_APPROVE, body);
    return response.data;
}

export async function bulkResolveQueue(body: CpmBulkResolveRequest): Promise<CpmBulkResult> {
    const response = await apiClient.post<Envelope<CpmBulkResult>>(CPM.QUEUE_BULK_RESOLVE, body);
    return response.data;
}

export async function resolveProductLink(body: CpmResolveLinkRequest) {
    const response = await apiClient.post<Envelope<unknown>>(CPM.QUEUE_RESOLVE, body);
    return response.data;
}

export async function dismissProductLink(body: { productLinkId: string; reason?: string }) {
    const response = await apiClient.patch<Envelope<unknown>>(CPM.QUEUE_DISMISS, body);
    return response.data;
}

export async function getRejectPreview(params: {
    level: string;
    masterId: string;
}): Promise<CpmRejectPreviewResponse> {
    const response = await apiClient.get<Envelope<CpmRejectPreviewResponse>>(
        `${CPM.QUEUE_REJECT_PREVIEW}?level=${encodeURIComponent(params.level)}&masterId=${encodeURIComponent(params.masterId)}`
    );
    return response.data;
}

export async function rejectQueueItem(body: CpmRejectRequest) {
    const response = await apiClient.post<Envelope<unknown>>(CPM.QUEUE_REJECT, body);
    return response.data;
}

export async function getNextFlagged(params: {
    afterId: string;
    method?: string;
    storeBrand?: string;
    dateFrom?: string;
    dateTo?: string;
}): Promise<{ next: { id: string; level: string } | null }> {
    const search = new URLSearchParams();
    search.set('afterId', params.afterId);
    if (params.method) search.set('method', params.method);
    if (params.storeBrand) search.set('storeBrand', params.storeBrand);
    if (params.dateFrom) search.set('dateFrom', params.dateFrom);
    if (params.dateTo) search.set('dateTo', params.dateTo);
    const response = await apiClient.get<Envelope<{ next: { id: string; level: string } | null }>>(
        `${CPM.QUEUE_NEXT_FLAGGED}?${search.toString()}`
    );
    return response.data;
}

export async function resolveBarcodeConflict(
    body: CpmBarcodeResolveRequest
): Promise<CpmBarcodeDryRunResult | Record<string, unknown>> {
    const response = await apiClient.post<
        Envelope<CpmBarcodeDryRunResult | Record<string, unknown>>
    >(CPM.QUEUE_BARCODE_RESOLVE, body);
    return response.data;
}

export async function getMasterDetails(
    body: CpmMasterDetailsRequest
): Promise<CpmMasterDetailsResponse> {
    const response = await apiClient.post<Envelope<CpmMasterDetailsResponse>>(CPM.DETAILS, body);
    return response.data;
}

export async function mergeMasters(body: CpmMergeRequest) {
    const response = await apiClient.post<Envelope<unknown>>(CPM.MERGE, body);
    return response.data;
}

export async function listMasters(body: CpmMasterListRequest): Promise<CpmMasterListResponse> {
    const response = await apiClient.post<Envelope<CpmMasterListResponse>>(CPM.LIST, body);
    return response.data;
}

export async function createMaster(body: Record<string, unknown>) {
    const response = await apiClient.post<
        Envelope<{ level: string; record: Record<string, unknown> }>
    >(CPM.CREATE, body);
    return response.data;
}

export async function updateMaster(body: Record<string, unknown>) {
    const response = await apiClient.patch<
        Envelope<{ level: string; record: Record<string, unknown> }>
    >(CPM.UPDATE, body);
    return response.data;
}

export async function deleteMaster(body: { level: string; id: string }) {
    const response = await apiClient.delete<
        Envelope<{ deleted: true; _id: string; level: string }>
    >(CPM.DELETE, { data: body });
    return response.data;
}

export async function bulkUpsertMasters(body: {
    dryRun: boolean;
    items: CpmBulkUpsertItem[];
}): Promise<CpmBulkUpsertResponse> {
    const response = await apiClient.post<Envelope<CpmBulkUpsertResponse>>(CPM.BULK_UPSERT, body);
    return response.data;
}

export async function listVolumeSettings(params?: {
    status?: string;
}): Promise<{ settings: CpmVolumeSetting[]; total: number }> {
    const response = await apiClient.post<
        Envelope<{ settings: CpmVolumeSetting[]; total: number }>
    >(CPM.VOLUME_SETTINGS.LIST, params ?? {});
    return response.data;
}

export async function createVolumeSetting(body: {
    value: number;
    unit: string;
}): Promise<{ setting: CpmVolumeSetting }> {
    const response = await apiClient.post<Envelope<{ setting: CpmVolumeSetting }>>(
        CPM.VOLUME_SETTINGS.CREATE,
        body
    );
    return response.data;
}

export async function updateVolumeSetting(body: {
    id: string;
    value?: number;
    unit?: string;
    status?: string;
}): Promise<{ setting: CpmVolumeSetting }> {
    const response = await apiClient.patch<Envelope<{ setting: CpmVolumeSetting }>>(
        CPM.VOLUME_SETTINGS.UPDATE,
        body
    );
    return response.data;
}

export async function deleteVolumeSetting(body: {
    id: string;
}): Promise<{ deleted: true; _id: string }> {
    const response = await apiClient.delete<Envelope<{ deleted: true; _id: string }>>(
        CPM.VOLUME_SETTINGS.DELETE,
        { data: body }
    );
    return response.data;
}

export async function getVolumeSettingUsage(body: {
    id: string;
}): Promise<{ usageCount: number; _id: string; value: number; unit: string }> {
    const response = await apiClient.post<
        Envelope<{ usageCount: number; _id: string; value: number; unit: string }>
    >(CPM.VOLUME_SETTINGS.USAGE, body);
    return response.data;
}

export async function listExciseCategories(params: {
    state?: string;
    page?: number;
    limit?: number;
}): Promise<{
    categories: CpmExciseCategory[];
    pagination: { page: number; limit: number; total: number; totalPages: number };
}> {
    const response = await apiClient.post<
        Envelope<{
            categories: CpmExciseCategory[];
            pagination: { page: number; limit: number; total: number; totalPages: number };
        }>
    >(CPM.EXCISE_CATEGORY.LIST, params);
    return response.data;
}

function unwrapStateRows(payload: unknown): CpmState[] {
    if (Array.isArray(payload)) {
        return payload.flatMap((row) => {
            if (typeof row === 'string' && row.trim()) {
                return [{ name: row.trim() } as CpmState];
            }
            if (row && typeof row === 'object') {
                const record = row as CpmState & { state?: string };
                const name = record.name ?? record.state;
                if (typeof name === 'string' && name.trim()) {
                    return [{ ...record, name: name.trim() }];
                }
            }
            return [];
        });
    }
    if (payload && typeof payload === 'object') {
        const record = payload as { states?: unknown; data?: unknown };
        if (record.states != null) return unwrapStateRows(record.states);
        if (record.data != null) return unwrapStateRows(record.data);
    }
    return [];
}

export async function listStates(): Promise<CpmState[]> {
    const response = await apiClient.get<Envelope<{ states: CpmState[] } | CpmState[]>>(
        CPM.STATE.LIST
    );
    return unwrapStateRows(response.data ?? response);
}

export async function updateStateLiveStatus(
    body: CpmUpdateStateLiveStatusRequest
): Promise<CpmState> {
    const response = await apiClient.patch<Envelope<CpmState>>(CPM.STATE.LIVE_STATUS, body);
    return response.data;
}

export async function updateStateLicenseTypes(
    body: CpmUpdateStateLicenseTypesRequest
): Promise<CpmState> {
    const response = await apiClient.patch<Envelope<CpmState>>(CPM.STATE.LICENSE_TYPES, body);
    return response.data;
}

export async function createExciseCategory(
    body: CpmCreateExciseCategoryRequest
): Promise<CpmExciseCategory> {
    const response = await apiClient.post<Envelope<CpmExciseCategory>>(
        CPM.EXCISE_CATEGORY.CREATE,
        body
    );
    return response.data;
}

export async function updateExciseCategory(
    body: CpmUpdateExciseCategoryRequest
): Promise<CpmExciseCategory> {
    const response = await apiClient.patch<Envelope<CpmExciseCategory>>(
        CPM.EXCISE_CATEGORY.UPDATE,
        body
    );
    return response.data;
}

export async function updateExciseCategoryStatus(
    body: CpmUpdateExciseCategoryStatusRequest
): Promise<CpmExciseCategory> {
    const response = await apiClient.patch<Envelope<CpmExciseCategory>>(
        CPM.EXCISE_CATEGORY.STATUS,
        body
    );
    return response.data;
}

export async function deleteExciseCategory(
    body: CpmDeleteExciseCategoryRequest
): Promise<{ deleted: true; _id: string }> {
    const response = await apiClient.delete<Envelope<{ deleted: true; _id: string }>>(
        CPM.EXCISE_CATEGORY.DELETE,
        { data: body }
    );
    return response.data;
}

export async function refreshExciseCategoryVolumes(
    body: CpmRefreshExciseCategoryVolumesRequest
): Promise<CpmExciseCategory> {
    const response = await apiClient.post<Envelope<CpmExciseCategory>>(
        CPM.EXCISE_CATEGORY.REFRESH_VOLUMES,
        body
    );
    return response.data;
}
